import { motion } from "framer-motion";
import {
    MapPin,
    Star,
    MessageCircle,
    Calculator,
    Search,
    Navigation,
    Users,
    Clock,
    Send,
    BadgeCheck,
    Bell,
    ChevronRight,
    Plus,
} from "lucide-react";

/**
 * Premium app-screen library. Each component renders the content of a phone
 * screen at 240x506 (9:19 ratio). Wrapped by <PhoneFrame> inside Screenshots.jsx.
 */

export function ScreenFeed() {
    return (
        <div className="h-full flex flex-col">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-[9px] uppercase tracking-[0.2em] text-slate-400">
                        Bonjour, Sarah
                    </p>
                    <h4 className="font-display font-black text-lg text-slate-900 leading-tight">
                        Trajets près de vous
                    </h4>
                </div>
                <span className="w-7 h-7 rounded-full bg-slate-50 border border-slate-100 flex items-center justify-center">
                    <Bell className="w-3.5 h-3.5 text-slate-600" strokeWidth={1.8} />
                </span>
            </div>

            <div className="mt-3 flex gap-1.5">
                {["Tous", "Aéroport", "Soirée"].map((c, i) => (
                    <span
                        key={c}
                        className={`text-[9px] px-2 py-1 rounded-full ${i === 0 ? "bg-coway text-white" : "bg-slate-50 text-slate-600 border border-slate-100"}`}
                    >
                        {c}
                    </span>
                ))}
            </div>

            <div className="mt-3 space-y-2 flex-1 overflow-hidden">
                {[
                    {
                        from: "Bastille",
                        to: "Gare du Nord",
                        time: "18:40",
                        seats: 2,
                        share: "6,40",
                    },
                    {
                        from: "Châtelet",
                        to: "CDG T2",
                        time: "06:15",
                        seats: 1,
                        share: "18,90",
                    },
                    {
                        from: "Opéra",
                        to: "Orly Sud",
                        time: "21:00",
                        seats: 3,
                        share: "14,20",
                    },
                ].map((t, i) => (
                    <div
                        key={i}
                        className="p-2.5 rounded-xl bg-white border border-slate-100 shadow-[0_1px_0_rgba(15,23,42,0.02)]"
                    >
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-coway" />
                                <span className="text-[10px] font-medium text-slate-900">
                                    {t.from}
                                </span>
                                <ChevronRight className="w-2.5 h-2.5 text-slate-300" />
                                <span className="text-[10px] font-medium text-slate-900">
                                    {t.to}
                                </span>
                            </div>
                            <span className="text-[10px] font-bold text-slate-900">
                                {t.share}€
                            </span>
                        </div>
                        <div className="mt-1.5 flex items-center justify-between">
                            <div className="flex items-center gap-1 text-[9px] text-slate-500">
                                <Clock className="w-2.5 h-2.5" strokeWidth={1.8} />
                                {t.time}
                                <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-coway-soft text-coway text-[8px] font-semibold">
                                    {t.seats} place{t.seats > 1 ? "s" : ""}
                                </span>
                            </div>
                            <div className="flex -space-x-1">
                                <span className="w-3.5 h-3.5 rounded-full bg-slate-300 border border-white" />
                                <span className="w-3.5 h-3.5 rounded-full bg-coway/40 border border-white" />
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <button className="mt-2 h-10 rounded-full bg-coway text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-[0_8px_20px_-8px_rgba(81,199,173,0.55)]">
                <Plus className="w-3.5 h-3.5" />
                Publier un trajet
            </button>
        </div>
    );
}

export function ScreenMap() {
    return (
        <div className="h-full flex flex-col">
            <div className="rounded-2xl overflow-hidden border border-slate-100 relative flex-1">
                <svg
                    viewBox="0 0 200 360"
                    className="absolute inset-0 w-full h-full"
                    preserveAspectRatio="xMidYMid slice"
                >
                    <defs>
                        <linearGradient id="map-bg" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#EEF3F6" />
                            <stop offset="100%" stopColor="#DCE5EB" />
                        </linearGradient>
                    </defs>
                    <rect width="200" height="360" fill="url(#map-bg)" />
                    {Array.from({ length: 12 }).map((_, i) => (
                        <line
                            key={`h-${i}`}
                            x1="0"
                            x2="200"
                            y1={i * 32}
                            y2={i * 32}
                            stroke="rgba(15,23,42,0.06)"
                        />
                    ))}
                    {Array.from({ length: 7 }).map((_, i) => (
                        <line
                            key={`v-${i}`}
                            y1="0"
                            y2="360"
                            x1={i * 32}
                            x2={i * 32}
                            stroke="rgba(15,23,42,0.06)"
                        />
                    ))}
                    {/* random blocks */}
                    <rect x="20" y="40" width="50" height="34" rx="3" fill="#FFFFFF" opacity=".8" />
                    <rect x="100" y="120" width="60" height="40" rx="3" fill="#FFFFFF" opacity=".8" />
                    <rect x="30" y="220" width="80" height="50" rx="3" fill="#FFFFFF" opacity=".8" />
                    <path
                        d="M30 290 Q 80 220 110 180 T 180 60"
                        stroke="#51c7ad"
                        strokeWidth="3"
                        fill="none"
                        strokeLinecap="round"
                    />
                    <circle cx="30" cy="290" r="6" fill="#fff" stroke="#51c7ad" strokeWidth="2.5" />
                    <circle cx="180" cy="60" r="6" fill="#51c7ad" />
                </svg>

                <div className="absolute top-2 left-2 right-2 flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/95 backdrop-blur-md shadow-sm border border-slate-100">
                    <Search className="w-3 h-3 text-slate-500" strokeWidth={2} />
                    <span className="text-[10px] text-slate-700">
                        Bastille → CDG
                    </span>
                </div>

                <div className="absolute bottom-2 left-2 right-2 rounded-2xl bg-white border border-slate-100 p-2.5 shadow-md">
                    <p className="text-[9px] uppercase tracking-wider text-slate-400">
                        Trajet sélectionné
                    </p>
                    <div className="flex items-center justify-between mt-1">
                        <span className="text-xs font-bold text-slate-900">
                            32 min · 18 km
                        </span>
                        <span className="text-xs font-bold text-coway">
                            -42%
                        </span>
                    </div>
                    <button className="mt-2 h-7 w-full rounded-full bg-coway text-white font-semibold text-[10px]">
                        Rejoindre ce trajet
                    </button>
                </div>
            </div>
        </div>
    );
}

export function ScreenCreate() {
    return (
        <div className="h-full flex flex-col">
            <p className="text-[9px] uppercase tracking-[0.2em] text-slate-400">
                Publier un trajet
            </p>
            <h4 className="font-display font-black text-lg text-slate-900 mt-0.5">
                Nouveau trajet
            </h4>

            <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-100">
                    <span className="w-2 h-2 rounded-full bg-coway" />
                    <div className="flex-1 text-[10px] text-slate-900 font-medium">
                        République, Paris 11e
                    </div>
                </div>
                <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-slate-50 border border-slate-100">
                    <span className="w-2 h-2 rounded-sm bg-slate-700" />
                    <div className="flex-1 text-[10px] text-slate-900 font-medium">
                        Aéroport CDG, T2E
                    </div>
                </div>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                    <p className="text-[9px] text-slate-400 uppercase tracking-wider">
                        Date
                    </p>
                    <p className="text-[10px] font-semibold text-slate-900 mt-0.5">
                        Mer. 14 mai
                    </p>
                </div>
                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                    <p className="text-[9px] text-slate-400 uppercase tracking-wider">
                        Heure
                    </p>
                    <p className="text-[10px] font-semibold text-slate-900 mt-0.5">
                        06:15
                    </p>
                </div>
            </div>

            <div className="mt-3 p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                <p className="text-[9px] text-slate-400 uppercase tracking-wider">
                    Places disponibles
                </p>
                <div className="mt-1.5 flex items-center gap-1.5">
                    {[1, 2, 3, 4].map((n) => (
                        <span
                            key={n}
                            className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold ${
                                n === 2
                                    ? "bg-coway text-white"
                                    : "bg-white text-slate-500 border border-slate-200"
                            }`}
                        >
                            {n}
                        </span>
                    ))}
                </div>
            </div>

            <button className="mt-auto h-10 rounded-full bg-coway text-white font-bold text-xs shadow-[0_8px_20px_-8px_rgba(81,199,173,0.55)]">
                Publier mon trajet
            </button>
        </div>
    );
}

export function ScreenTripDetail() {
    return (
        <div className="h-full flex flex-col">
            <p className="text-[9px] uppercase tracking-[0.2em] text-slate-400">
                Détail du trajet
            </p>
            <h4 className="font-display font-black text-lg text-slate-900 leading-tight">
                Bastille → CDG T2
            </h4>

            <div className="mt-3 rounded-xl bg-slate-50 border border-slate-100 p-2.5">
                <div className="flex items-start gap-2">
                    <div className="flex flex-col items-center pt-0.5">
                        <span className="w-2 h-2 rounded-full bg-coway" />
                        <span className="w-px h-6 bg-slate-200 my-0.5" />
                        <span className="w-2 h-2 rounded-sm bg-slate-700" />
                    </div>
                    <div className="flex-1">
                        <p className="text-[10px] font-semibold text-slate-900">
                            06:15 — Bastille
                        </p>
                        <p className="text-[9px] text-slate-500">
                            République, Paris 11e
                        </p>
                        <p className="text-[10px] font-semibold text-slate-900 mt-1.5">
                            06:55 — CDG T2
                        </p>
                        <p className="text-[9px] text-slate-500">
                            Roissy-en-France
                        </p>
                    </div>
                </div>
            </div>

            <p className="text-[9px] uppercase tracking-wider text-slate-400 mt-3 mb-1.5">
                Passagers (3)
            </p>
            <div className="space-y-1.5">
                {[
                    { n: "Léa", r: "Conductrice" },
                    { n: "Karim", r: "Passager" },
                    { n: "Vous", r: "Passager" },
                ].map((p, i) => (
                    <div
                        key={i}
                        className="flex items-center justify-between p-2 rounded-lg bg-white border border-slate-100"
                    >
                        <div className="flex items-center gap-1.5">
                            <span
                                className={`w-5 h-5 rounded-full ${i % 2 === 0 ? "bg-coway/30" : "bg-slate-200"} flex items-center justify-center text-[9px] font-bold text-slate-700`}
                            >
                                {p.n[0]}
                            </span>
                            <span className="text-[10px] font-medium text-slate-900">
                                {p.n}
                            </span>
                        </div>
                        <span className="text-[9px] text-slate-400">
                            {p.r}
                        </span>
                    </div>
                ))}
            </div>

            <button className="mt-auto h-10 rounded-full bg-coway text-white font-bold text-xs flex items-center justify-center gap-1.5">
                <MessageCircle className="w-3.5 h-3.5" />
                Ouvrir le chat
            </button>
        </div>
    );
}

export function ScreenChat() {
    const msgs = [
        { me: false, t: "Salut ! Toujours OK pour 06:15 demain ?" },
        { me: true, t: "Yes, je suis devant le métro Bastille 🙂" },
        { me: false, t: "Parfait, je serai en retard de 2 min max." },
        { me: true, t: "Pas de souci, à demain !" },
    ];
    return (
        <div className="h-full flex flex-col">
            <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                <span className="w-7 h-7 rounded-full bg-coway/30 flex items-center justify-center text-[10px] font-bold text-slate-700">
                    L
                </span>
                <div>
                    <p className="text-[11px] font-bold text-slate-900 leading-none">
                        Léa M.
                    </p>
                    <p className="text-[9px] text-slate-500 mt-0.5 flex items-center gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-coway" />
                        En ligne
                    </p>
                </div>
            </div>

            <div className="mt-3 flex-1 flex flex-col gap-1.5 overflow-hidden">
                {msgs.map((m, i) => (
                    <div
                        key={i}
                        className={`flex ${m.me ? "justify-end" : "justify-start"}`}
                    >
                        <div
                            className={`max-w-[78%] px-2.5 py-1.5 rounded-2xl text-[10px] leading-snug ${
                                m.me
                                    ? "bg-coway text-white rounded-br-md"
                                    : "bg-slate-50 text-slate-900 rounded-bl-md border border-slate-100"
                            }`}
                        >
                            {m.t}
                        </div>
                    </div>
                ))}
            </div>

            <div className="mt-2 flex items-center gap-1.5 p-1.5 rounded-full bg-slate-50 border border-slate-100">
                <input
                    disabled
                    placeholder="Écrire un message..."
                    className="flex-1 bg-transparent text-[10px] text-slate-700 outline-none px-2"
                />
                <button className="w-7 h-7 rounded-full bg-coway flex items-center justify-center">
                    <Send className="w-3 h-3 text-white" strokeWidth={2.2} />
                </button>
            </div>
        </div>
    );
}

export function ScreenProfile() {
    return (
        <div className="h-full flex flex-col">
            <div className="flex flex-col items-center">
                <div className="w-14 h-14 rounded-full bg-coway/25 border-2 border-coway flex items-center justify-center text-base font-bold text-slate-700">
                    L
                </div>
                <p className="text-sm font-bold text-slate-900 mt-2 leading-none">
                    Léa Martin
                </p>
                <p className="text-[10px] text-slate-500 mt-1 flex items-center gap-1">
                    <BadgeCheck className="w-3 h-3 text-coway" strokeWidth={2} />
                    Profil vérifié
                </p>
                <div className="mt-2 flex items-center gap-1">
                    {Array.from({ length: 5 }).map((_, i) => (
                        <Star
                            key={i}
                            className="w-3 h-3 fill-coway text-coway"
                            strokeWidth={0}
                        />
                    ))}
                    <span className="text-[10px] text-slate-700 font-semibold ml-1">
                        4.9
                    </span>
                </div>
            </div>

            <div className="mt-3 grid grid-cols-3 gap-1.5 text-center">
                {[
                    { v: "32", l: "Trajets" },
                    { v: "98%", l: "Fiables" },
                    { v: "2024", l: "Membre" },
                ].map((s) => (
                    <div
                        key={s.l}
                        className="p-2 rounded-xl bg-slate-50 border border-slate-100"
                    >
                        <p className="text-xs font-bold text-slate-900 leading-none">
                            {s.v}
                        </p>
                        <p className="text-[8px] text-slate-500 mt-1 uppercase tracking-wider">
                            {s.l}
                        </p>
                    </div>
                ))}
            </div>

            <p className="text-[9px] uppercase tracking-wider text-slate-400 mt-3 mb-1.5">
                Avis récents
            </p>
            <div className="space-y-1.5 flex-1 overflow-hidden">
                {[
                    { n: "Karim", t: "Sympa et ponctuelle, top !" },
                    { n: "Sarah", t: "Trajet très agréable, je recommande." },
                ].map((r, i) => (
                    <div
                        key={i}
                        className="p-2 rounded-xl bg-white border border-slate-100"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-[10px] font-semibold text-slate-900">
                                {r.n}
                            </span>
                            <div className="flex">
                                {Array.from({ length: 5 }).map((_, j) => (
                                    <Star
                                        key={j}
                                        className="w-2 h-2 fill-coway text-coway"
                                        strokeWidth={0}
                                    />
                                ))}
                            </div>
                        </div>
                        <p className="text-[9px] text-slate-500 mt-0.5 leading-snug">
                            {r.t}
                        </p>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function ScreenSearch() {
    return (
        <div className="h-full flex flex-col">
            <p className="text-[9px] uppercase tracking-[0.2em] text-slate-400">
                Rechercher
            </p>
            <h4 className="font-display font-black text-lg text-slate-900 leading-tight">
                Où allez-vous ?
            </h4>

            <div className="mt-3 space-y-2">
                <div className="flex items-center gap-2.5 px-3 py-3 rounded-xl bg-slate-50 border border-slate-100">
                    <MapPin className="w-3.5 h-3.5 text-coway" strokeWidth={2} />
                    <div className="flex-1">
                        <p className="text-[9px] text-slate-400 uppercase tracking-wider">
                            Départ
                        </p>
                        <p className="text-[10px] font-medium text-slate-900">
                            Position actuelle
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2.5 px-3 py-3 rounded-xl bg-slate-50 border border-slate-100">
                    <Navigation className="w-3.5 h-3.5 text-coway" strokeWidth={2} />
                    <div className="flex-1">
                        <p className="text-[9px] text-slate-400 uppercase tracking-wider">
                            Arrivée
                        </p>
                        <p className="text-[10px] font-medium text-slate-900">
                            Aéroport CDG
                        </p>
                    </div>
                </div>
            </div>

            <p className="text-[9px] uppercase tracking-wider text-slate-400 mt-3 mb-1.5">
                Suggestions
            </p>
            <div className="space-y-1.5 flex-1 overflow-hidden">
                {["Gare de Lyon", "Orly Sud", "Disneyland Paris"].map((s) => (
                    <div
                        key={s}
                        className="flex items-center gap-2 p-2 rounded-lg bg-white border border-slate-100"
                    >
                        <Search className="w-3 h-3 text-slate-400" strokeWidth={2} />
                        <span className="text-[10px] text-slate-900">{s}</span>
                    </div>
                ))}
            </div>

            <button className="mt-2 h-10 rounded-full bg-coway text-white font-bold text-xs flex items-center justify-center gap-1.5 shadow-[0_8px_20px_-8px_rgba(81,199,173,0.55)]">
                <Users className="w-3.5 h-3.5" />
                Trouver mon Coway
            </button>
        </div>
    );
}

export function ScreenReviews() {
    return (
        <div className="h-full flex flex-col">
            <p className="text-[9px] uppercase tracking-[0.2em] text-slate-400">
                Évaluation
            </p>
            <h4 className="font-display font-black text-lg text-slate-900 leading-tight">
                Comment était votre trajet ?
            </h4>

            <div className="mt-3 p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                <div className="flex items-center gap-2">
                    <span className="w-7 h-7 rounded-full bg-coway/30 flex items-center justify-center text-[10px] font-bold text-slate-700">
                        L
                    </span>
                    <div>
                        <p className="text-[10px] font-semibold text-slate-900">
                            Léa M.
                        </p>
                        <p className="text-[9px] text-slate-500">
                            Bastille → CDG · ce matin
                        </p>
                    </div>
                </div>
            </div>

            <div className="mt-3 flex items-center justify-center gap-1.5">
                {Array.from({ length: 5 }).map((_, i) => (
                    <Star
                        key={i}
                        className={`w-6 h-6 ${i < 5 ? "fill-coway text-coway" : "text-slate-200"}`}
                        strokeWidth={0}
                    />
                ))}
            </div>
            <p className="text-center text-[10px] text-slate-700 font-semibold mt-1">
                Excellent
            </p>

            <p className="text-[9px] uppercase tracking-wider text-slate-400 mt-3 mb-1.5">
                Compliments
            </p>
            <div className="flex flex-wrap gap-1.5">
                {["Ponctuelle", "Sympa", "Voiture propre", "Bonne discussion"].map(
                    (c, i) => (
                        <span
                            key={c}
                            className={`text-[9px] px-2 py-1 rounded-full border ${
                                i < 2
                                    ? "bg-coway-soft border-coway/20 text-coway font-semibold"
                                    : "bg-white border-slate-200 text-slate-600"
                            }`}
                        >
                            {c}
                        </span>
                    ),
                )}
            </div>

            <button className="mt-auto h-10 rounded-full bg-coway text-white font-bold text-xs">
                Envoyer mon avis
            </button>
        </div>
    );
}

export function PhoneFrame({ children, className = "", fullBleed = false, src = null, alt = "" }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className={`relative w-[220px] sm:w-[240px] lg:w-[260px] aspect-[9/19.5] rounded-[2.5rem] bg-slate-900 border border-slate-800 shadow-[0_30px_60px_-15px_rgba(15,23,42,0.22),0_0_60px_-20px_rgba(81,199,173,0.15)] overflow-hidden shrink-0 ${className}`}
        >
            <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-20 h-5 bg-black rounded-full z-20" />
            {src ? (
                <div className="absolute inset-[5px] rounded-[2.2rem] overflow-hidden bg-white flex items-center justify-center">
                    <img
                        src={src}
                        alt={alt}
                        loading="lazy"
                        className="w-full h-full object-contain object-center select-none pointer-events-none"
                        draggable={false}
                    />
                </div>
            ) : (
                <div className={`absolute inset-1 rounded-[2.3rem] overflow-hidden bg-white ${fullBleed ? "" : "px-3 pt-8 pb-3"} flex flex-col`}>
                    {!fullBleed && (
                        <div className="flex items-center justify-between text-[8px] text-slate-500 font-medium mb-1.5">
                            <span>9:41</span>
                            <span className="flex items-center gap-1">
                                <span className="w-2.5 h-1 rounded-sm bg-slate-300" />
                                <span className="w-1 h-1 rounded-sm bg-coway" />
                            </span>
                        </div>
                    )}
                    <div className="flex-1 overflow-hidden">{children}</div>
                </div>
            )}
        </motion.div>
    );
}
